import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import useHeaderHeight from '../../hooks/useHeaderHeight';
import { sendMagicLink, submitMagicLinkCode } from '../../redux/middleware/users';
import './LoginModal.css';

const LoginModal = () => {
  const dispatch = useDispatch();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Code-entry mode: the iOS-PWA path. The emailed link opens in Safari (a
  // separate cookie jar), so Donovan can instead type the 6-digit code that
  // rides along with the link and sign in without leaving the app.
  const [codeMode, setCodeMode] = useState(false);
  const [code, setCode] = useState('');

  useHeaderHeight();

  // No email input — Donovan is the only admin, so the backend already knows
  // where to send the link. Nothing about the address is exposed in the UI.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await dispatch(sendMagicLink());
      setSent(true);
    } catch (err) {
      console.error(err);
      setError('Could not send the login link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      // On success the thunk sets the session user and ProtectedRoute swaps in
      // the /secure dashboard — no navigation needed here.
      await dispatch(submitMagicLinkCode(code.trim()));
    } catch (err) {
      setError(err.message || 'That code is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="login-backdrop" />
      <div className="login-modal">
        {codeMode ? (
          <>
            <p className="magic-link-heading">Enter your login code</p>
            <p className="magic-link-sub">
              Type the 6-digit code from your login email.
            </p>
            {error && <div role="alert" className="error">{error}</div>}
            <form onSubmit={handleCodeSubmit}>
              <div className="form-group">
                <input
                  className="code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                />
              </div>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Verifying…' : 'Log in with code'}
              </button>
            </form>
            <button
              type="button"
              className="magic-link-resend"
              onClick={() => { setCodeMode(false); setError(''); }}
              disabled={submitting}
            >
              Back to login link
            </button>
          </>
        ) : !sent ? (
          <>
            {error && <div role="alert" className="error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send me the login link'}
              </button>
            </form>
            <button
              type="button"
              className="magic-link-resend"
              onClick={() => { setCodeMode(true); setError(''); }}
              disabled={submitting}
            >
              I have a code
            </button>
          </>
        ) : (
          <div className="magic-link-sent">
            <p className="magic-link-heading">Check your email</p>
            <p className="magic-link-sub">
              We sent you a login link and a 6-digit code. It expires in 15
              minutes — click the link, or enter the code below if you're on
              your phone.
            </p>
            <button
              type="button"
              className="magic-link-resend"
              onClick={() => { setCodeMode(true); setError(''); }}
              disabled={submitting}
            >
              Enter the code
            </button>
            <button
              type="button"
              className="magic-link-resend"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Send another'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default LoginModal;
